/**
 * 화면이 다루는 타입.
 *
 * v1 의 `lib/types.ts` 는 Supabase 테이블 행을 그대로 옮긴 것이라 필드가 snake_case 였다.
 * v2 는 브라우저가 DB 를 보지 않고 백엔드 DTO 만 보므로, 여기 타입은 **API 응답 모양**이다.
 * 백엔드 Jackson 이 기본 설정(camelCase)이므로 필드도 camelCase 로 맞춘다.
 */

export type UserType = 'super_admin' | 'admin' | 'user';

/**
 * 계정 종류를 사람 말로. <b>화면에 {@code userType} 을 날것으로 찍지 않는다</b> —
 * 서버가 내려보내는 값은 API 계약이라 영어 소문자다(`UserType.value()`), 그것을 그대로 뱃지에
 * 넣으면 사용자에게는 「super_admin」 이라는 정체 모를 글자가 뜬다(사용자 지적 2026-09-03 · 앱).
 *
 * <p>웹과 앱이 <b>같은 표</b>를 본다. 각자 지으면 같은 계정이 화면마다 다른 이름으로 불린다.
 */
export const USER_TYPE_LABEL: Record<UserType, string> = {
  super_admin: '최고 관리자',
  admin: '관리자',
  user: '일반 사용자',
};

/**
 * 관리자(전체 메뉴 열람·관리) 여부. `super_admin`·`admin` 이 관리자다.
 *
 * 관리자에게 전 메뉴를 여는 판단은 프론트가 한다 — 전체 메뉴 키의 우주가 `menuConfig.ts` 에 있고
 * 서버는 그것을 열거하지 않기 때문이다. 서버의 `visibleMenuKeys` 는 비관리자용 목록만 담아 내려온다.
 */
export function isAdministrator(userType: UserType | undefined): boolean {
  return userType === 'super_admin' || userType === 'admin';
}

/**
 * 최고 관리자(운영자) 여부. 서버 상태·오류를 다루는 운영 화면(관리자 대시보드)은 화면 관리자(admin)가 아니라
 * `super_admin` 만 연다 — 백엔드도 같은 방침으로 역할(SUPER_ADMIN)로 가른다.
 */
export function isSuperAdmin(userType: UserType | undefined): boolean {
  return userType === 'super_admin';
}

export type SessionUser = {
  id: string;
  email: string;
  name: string;
  rank: string | null;
  userType: UserType;
  mustChangePassword: boolean;
  /**
   * 부여된 역할 코드(규칙부여 ∪ 수동부여 − 제외). 로그인 시 서버가 산출한 값이라 역할을 방금
   * 받았으면 다시 로그인해야 채워진다.
   *
   * 메뉴 권한은 이미 `menuPermissions` 로 풀려 오므로 화면이 역할 코드를 볼 일은 원칙적으로 없다.
   * 예외는 **관리자 우회를 끊어야 하는 자격**이다 — 인사팀(`lib/auth/hrTeam.ts`)처럼 `userType`
   * 으로 열면 안 되는 판정만 이 목록을 본다.
   */
  roles?: string[];
};

/** `GET /api/v1/auth/session` 응답 본문. */
export type Session = {
  authenticated: boolean;
  user: SessionUser | null;
  /**
   * 이 계정에 보여줄 메뉴 키. 화면 표시용일 뿐 인가가 아니다.
   * 실제 접근 판정은 각 API 가 매 요청마다 다시 한다.
   */
  visibleMenuKeys: string[];
  /**
   * 메뉴별 동작 권한(등록·수정·삭제 버튼 노출 판단용). `visibleMenuKeys` 와 마찬가지로 표시용이며
   * 인가가 아니다 — 실제 판정은 서버가 매 요청마다 한다. 관리자는 빈 목록으로 오고, 프론트가
   * `userType` 으로 전부 연다(`useCanManage`). `menuKey` 는 `canView` 인 것만 `visibleMenuKeys` 에도 담긴다.
   */
  menuPermissions: MenuPermission[];
  /**
   * **아직 열지 않은 메뉴**(오픈 게이트 · 백엔드 `MenuGate` · V153).
   *
   * 위 두 목록에서는 서버가 이미 빼고 내려주므로 링크 하나를 감추는 데는 필요 없다. 그런데
   * **권한을 갖지 않는 것들**이 있다 — 그룹(자료실·자산관리)은 권한 키가 없고, AI Agent 버튼은
   * 메뉴가 아니라 헤더에 산다. 그것들을 감추는 판정이 이 목록이다.
   *
   * 관리자(admin·super_admin)에게는 **적용하지 않는다** — 닫아 둔 화면을 만들고 시험하는 사람이
   * 곧 관리자이고, 게이트를 되돌리는 화면(환경설정)까지 함께 잠기면 안 된다.
   *
   * 예전 응답에는 없던 값이라 없을 수 있다(테스트 고정값 포함). 없으면 「아무것도 안 닫혔다」로 본다.
   */
  closedMenuKeys?: string[];
  /**
   * **모든 현장에 쓸 수 있는가** — 관리자이거나 서버 `ScopeResolver` 가 제한 없음으로 본 사람.
   *
   * 예전 응답에는 없던 값이라 없을 수 있다. 없으면 「제한 없음」으로 본다 — 이 값이 인가가 아니라
   * **버튼 노출 판단**이기 때문이다. 실제 판정은 서버 `SiteScopeGuard` 가 매 요청마다 다시 하므로,
   * 없는 값 때문에 버튼이 뜨더라도 남의 현장 저장은 그대로 403 이다. 반대로 기본을 「제한 있음」으로
   * 두면 옛 응답을 쓰는 화면에서 **아무도 아무 데도 못 쓰게** 된다.
   */
  siteScopeAll?: boolean;
  /**
   * 쓸 수 있는 현장 **이름**. `siteScopeAll` 이 참이면 무시한다.
   *
   * 서버가 현장명으로 견주므로(`SiteScopeGuard`) id 가 아니라 이름이다 — 화면도 같은 열쇠로
   * 맞춰야 두 판정이 어긋나지 않는다.
   */
  siteScopeNames?: string[];
  /**
   * 같은 현장을 **마스터의 id** 로. `siteScopeAll` 이 참이면 비어 온다.
   *
   * **판정에 쓰지 않는다.** 서버의 문은 이름으로 견주고(`siteScopeNames`) 그 규약은 그대로다.
   * 이 값은 화면이 **「내 현장」을 짚는 데**만 쓴다 — 현장 정보가 그 현장을 앞으로 올리고 배지를 단다.
   *
   * 이름으로 짚으면 「염창동 현장」·「염창동현장」 한 칸 차이로 조용히 빗나가는데, 그 실패는 화면에
   * 「내 현장이 앞에 안 오네」 로만 보여 원인을 짚을 수 없다. id 는 서버가 이미 마스터에 맞춰 낸 값이다.
   *
   * 마스터에 없는 이름은 id 가 없어 **이름 목록의 부분집합**이다. 옛 응답에는 이 칸이 없다.
   */
  siteScopeIds?: string[];
  /**
   * 지금 **배치돼 있는** 현장 이름 — 본사 인원이어도 접지 않는다 (사용자 결정 2026-09-30).
   *
   * `siteScopeNames` 는 열람 규칙이라 본사 인원에게는 비어 오는데, 현장 마스터의 [수정] 은 본사
   * 직원도 자기가 서 있는 현장에서만 떠야 한다. 관리자는 이 목록과 무관하게 전부다.
   * 옛 응답에는 없다 — 그때는 버튼이 안 뜰 뿐이고, 저장 판정은 서버가 한다.
   */
  assignedSiteNames?: string[];
  /**
   * 로그인이 **아직 끝나지 않았다** — 비밀번호는 맞았지만 처음 보는 브라우저라 서버가 인증 갈래(휴대폰 · 메일)를
   * 묻고 있고 세션은 열지 않았다(DOC-138). 이때 `authenticated` 는 false 다. 로그인 화면은 이 칸이 있으면
   * 갈래를 골라 `POST /auth/login/device-challenge` 로 번호를 받고 `/login/verify-device` 로 마무리한다.
   *
   * `pendingId` 는 「비밀번호를 맞힌 사람」 의 표라 두 요청이 들고 간다. `channels` 가 비어 오는 일은 없다 —
   * 고를 길이 없으면 서버가 묻지 않고 그냥 연다. 평소와 옛 서버 응답에는 이 칸이 없다.
   */
  deviceChallenge?: DeviceChallenge | null;
};

export type DeviceChannelKind = 'PHONE' | 'EMAIL';

export type DeviceChallenge = {
  pendingId: string;
  /**
   * 고를 수 있는 길과 가린 받는 곳(`010-****-1234` · `yo***@bomienc.com`).
   * 로그인 OTP 를 켠 사람(V618)은 `TOTP` 하나만 온다 — 보낼 것이 없어 `/login/verify-totp` 로 바로 간다.
   */
  channels: { kind: DeviceChannelKind | 'TOTP'; masked: string | null }[];
  /** 갈래를 고르고 번호를 맞히는 데 주는 시간(초). 지나면 다시 로그인한다. */
  expiresInSeconds: number;
};

export type MenuItemType = 'link' | 'group' | 'external';

/**
 * 이 설정이 어디서 온 값인지.
 *
 * - `admin` — 관리자가 메뉴 관리 화면에서 실제로 바꾼 값. 순서·배치까지 그대로 따른다.
 * - `v1_import` — v1 덤프가 실어 온 값. **라벨만** 쓰고 순서·배치는 v2 골격(`DEFAULT_MENU_CONFIG`)을 따른다.
 *
 * 구별이 없던 동안에는 덤프를 뜰 때마다 사이드바가 v1 순서로 되돌아갔다 — 실려 온 값과
 * 관리자가 정한 값이 같은 무게를 가졌기 때문이다. 판정은 서버가 한다(`MenuSettingService.replaceAll`).
 */
export type MenuSettingOrigin = 'admin' | 'v1_import';

/** `GET /api/v1/menu-settings` 응답 항목. 관리자가 라벨·순서를 바꾼 결과다. */
export type MenuSetting = {
  id: string;
  menuKey: string;
  label: string;
  groupKey: string | null;
  sortOrder: number;
  itemType: MenuItemType;
  /** 예전 응답에는 없던 값이라 없을 수 있다. 없으면 관리자가 정한 값으로 본다(종전 동작). */
  origin?: MenuSettingOrigin;
};

export type Week = {
  id: string;
  year: number;
  month: number;
  weekNumber: number;
  startDate: string;
  endDate: string;
  label: string;
};

export type Department = {
  id: string;
  name: string;
  sortOrder: number;
  active: boolean;
  /**
   * 업무 일정(대시보드·주간업무·월간업무)에서 고를 수 있는 부서인지.
   *
   * `active` 와 축이 다르다 — `active` 를 끄면 조직도·직원찾기에서도 사라지지만, 이것을 끈 부서는
   * 조직에는 그대로 서고 업무를 배정하는 자리에서만 빠진다(「경영진」 처럼 중점업무를 적지 않는 부서).
   * 지난 기록의 부서명을 찾는 곳(달력·주차 보기)에서는 이 값으로 거르지 않는다.
   */
  showInWork: boolean;
  /**
   * 상위 부서(V387). 비어 있으면 조직도의 뿌리다 — 직원찾기·인력배치표의 조직도가 이 값으로 트리를
   * 세운다. 같은 부모 안의 좌우 순서는 `sortOrder` 가 그대로 정한다.
   */
  parentId: string | null;
  /**
   * 조직도에서 따로 서는 자리(V389). `TOP` 은 맨 윗줄에 독립해서(감사실), `SIDE` 는 오른쪽 구석에
   * (현장준비팀). null 이면 조직 안 — 상위 부서 아래다. 자리를 고르면 상위 부서는 비워진다.
   */
  chartSlot: 'TOP' | 'SIDE' | null;
  /** 조직도에서 계산한 자리로부터 손으로 옮긴 거리(px · V391). 0 이면 그대로. */
  chartDx: number;
  chartDy: number;
};

export type SiteCategory = '국내진행현장' | '국내계약현장' | '해외진행현장' | '해외검토현장';
/** `ref_sites.status`. 평가(V472)는 사용승인일 + 필증이 들어오면 서버가 넣는다 — 사람은 넣지도 빼지도 못한다. */
export type SiteStatus = '진행' | '평가' | '미진행' | '준공';

export const SITE_CATEGORIES: SiteCategory[] = [
  '국내진행현장',
  '국내계약현장',
  '해외진행현장',
  '해외검토현장',
];

/** 현장 상태 선택 순서. 현장 관리·현장 조회 편집기가 공유한다. */
export const SITE_STATUSES: SiteStatus[] = ['진행', '평가', '미진행', '준공'];

/**
 * 상태를 화면에 적는 글자. 저장값 「미진행」 은 「예정」 으로 적는다(사용자 지시 2026-10-16) —
 * 값은 DB · 앱 · 서버가 함께 쥐고 있어 바꾸지 않고, 보이는 글자만 바꾼다.
 */
export function siteStatusLabel(status: string): string {
  return status === '미진행' ? '예정' : status;
}

/**
 * 시설 구분 — <b>건축법 시행령 별표1 「용도별 건축물의 종류」의 법정 용도명</b>
 * (사용자 지시 2026-09-02).
 *
 * 예전에는 아무 글자나 넣을 수 있어서 같은 아파트 현장이 「주거용」·「공동주택」·「아파트」로
 * 제각각 적혀 있다. 사람 눈에는 같은 말이지만 <b>질의에는 다른 값</b>이다. 2026-09-01 에
 * 드롭다운으로 막았지만 그 목록은 우리가 지어낸 이름이었다 — 「의료·복지시설」·「복합시설」은
 * 어느 법에도 없어 <b>건축물대장·허가서와 대조할 수 없고</b>, 경계를 다툴 근거가 우리 안에만
 * 있었다. 그래서 법정 명칭을 그대로 싣는다: 고르는 사람은 대장에 적힌 용도를 옮기면 된다.
 *
 * <b>이 목록은 제안이다 — 강제하지 않는다</b>(V207 · 사용자 지시 2026-09-02). 입력칸은 자유
 * 텍스트이고 이 목록은 그 옆에 붙어 고를 수 있게만 한다(`<datalist>`). 서버도 어떤 글자든 받는다.
 *
 * 강제를 푼 이유는 <b>이 칸이 지고 있던 짐이 옮겨 갔기</b> 때문이다. 준공용역 발주 대상을 하루
 * 전까지 이 값으로 가렸는데, 이제 현장의 스위치(`completionServiceAlert`)가 가린다. 표기가 갈려도
 * 알림을 놓치지 않는다.
 *
 * 인사 기준표(현장 기준인원)·착공 서류 기준·WBS 템플릿은 아직 같은 값으로 현장을 찾는다 —
 * 그래서 목록을 계속 권한다. 다만 거기서 안 걸리면 <b>빈 목록이 화면에 그대로 보인다.</b>
 *
 * <b>임대·분양 축은 없앴다</b>(2026-09-02). 임대/분양은 법정 용도가 아니라 공급방식이라, 한 칸에
 * 두 축을 섞고 있었다.
 *
 * 별표1 에 없는 값이 하나 있다 — 「건축물 아님(토목·플랜트 등)」. 토목 현장은 건축물이 아니라
 * 별표1 에 자리가 없다.
 *
 * 백엔드 `SiteCodes.FACILITY_TYPES` 와 <b>같은 글자·같은 순서</b>(별표1 의 호 순서)여야 한다.
 */
export const FACILITY_TYPES = [
  '단독주택',
  '공동주택',
  '제1종 근린생활시설',
  '제2종 근린생활시설',
  '문화 및 집회시설',
  '종교시설',
  '판매시설',
  '운수시설',
  '의료시설',
  '교육연구시설',
  '노유자시설',
  '수련시설',
  '운동시설',
  '업무시설',
  '숙박시설',
  '위락시설',
  '공장',
  '창고시설',
  '위험물 저장 및 처리 시설',
  '자동차 관련 시설',
  '동물 및 식물 관련 시설',
  '자원순환 관련 시설',
  '교정시설',
  '국방·군사시설',
  '방송통신시설',
  '발전시설',
  '묘지 관련 시설',
  '관광 휴게시설',
  '장례시설',
  '야영장 시설',
  '건축물 아님(토목·플랜트 등)',
] as const;

export type FacilityType = (typeof FACILITY_TYPES)[number];

export type Site = {
  id: string;
  name: string;
  /**
   * 현장 코드 — 입주민 앱의 첫 화면이 받는 여섯 글자 (V346 · DOC-95 §4-1).
   *
   * ★ **읽기 전용이다.** 서버가 발급하고 `SiteRequest` 에는 이 칸이 없다 — 종이·문자로 나눠 준
   * 값이라 현장 정보를 고쳤다고 바뀌면 입주민이 못 들어온다.
   */
  siteCode: string;
  siteCategory: SiteCategory;
  status: SiteStatus;
  /**
   * 수주 단계 — `BID · CONTRACT · LAUNCH · ACTIVE` (V303). 현장 현황이 이 값으로 두 보드로 갈린다.
   * 분류(국내/해외)와 축이 다르다: 저쪽은 어디인가, 이쪽은 어디까지 왔는가다.
   *
   * ★ 공용 목록(`useSites`)에는 **입찰 단계가 오지 않는다** — 서버가 바닥에서 걸러 낸다.
   */
  pipelineStage: 'BID' | 'CONTRACT' | 'LAUNCH' | 'ACTIVE' | null;
  contractPeriod: string | null;
  contractStartDate: string | null;
  contractEndDate: string | null;
  actualStartDate: string | null;
  /** 계약 날인일. 이 날부터 착공까지가 착공 준비 창이다(V118 · DOC-61 §9). */
  contractSignedOn: string | null;
  /** 착공 예정일. `actualStartDate` 는 착공한 뒤에나 채워지므로 D-day 는 이 칸으로 센다. */
  scheduledStartOn: string | null;
  scheduledCompletionDate: string | null;
  targetCompletionDate: string | null;
  /** 단축 목표일 — 서버 계산(DOC-118 §23). 옛 서버는 보내지 않는다. */
  shortenTargetDate?: string | null;
  /** 사용승인일 — 준공 탭의 사용승인 줄 실적일(DOC-118 §24). 목표 준공일과 별개다. 재시작 전 서버는 안 보낸다. */
  useApprovalDate?: string | null;
  /** 그 현장에 따로 정한 단축 목표 %(DOC-118 §25). 비면 회사 기본값. 최고관리자만 바꾼다. */
  scheduleShortenRate?: number | null;
  /** 실제로 쓰는 단축 목표 % — 따로 정한 값, 없으면 회사 기본값. 재시작 전 서버는 안 보낸다. */
  effectiveShortenRate?: number | null;
  scale: string | null;
  contractAmount: string | null;
  floorCount: string | null;
  grossAreaSqm: number | null;
  facilityType: string | null;
  /** 준공용역 발주 알림을 이 현장에 띄울지. 시설 구분이 아니라 이 값이 대상을 가린다(V207). */
  completionServiceAlert: boolean;
  meetingColor: string | null;
  siteAddress: string | null;
  clientName: string | null;
  supervisorName: string | null;
  designerName: string | null;
  nasLinkUrl: string | null;
  contractSummaryPdfName: string | null;
  /** presigned URL. 공개 버킷을 만들지 않는다는 규칙에 따라 만료되는 링크다. */
  contractSummaryPdfUrl: string | null;
  /**
   * 대표 이미지 — 현장 정보 카드에 보이는 사진. 현장 조회에서 직접 관리한다.
   * 서버: presigned URL(오브젝트 키는 `photo_key`). 로컬 목: 브라우저가 만든 data URL.
   */
  representativeImageUrl: string | null;
  /**
   * 제주·도서 등 **특별지역**. 참이면 귀향여비를 구간표로 계산하지 않고 **별도로 지급**한다.
   *
   * 주소가 제주면 저장할 때 서버가 켜지만 끌 수 있다 — 도서(울릉·백령)는 주소로 가릴 수 없어
   * 결국 사람이 켜야 한다.
   */
  specialArea: boolean;
  sortOrder: number;
  active: boolean;
  /**
   * 현장 좌표(위도 · 경도). 현장 정보에서 넣거나, 비어 있으면 세계 지도를 열 때 주소로 채워진다. 주소를 바꾸고
   * 좌표를 그대로 두면 서버가 옛 좌표를 지운다. 사용자 서버가 옛 판이면 오지 않는다.
   */
  latitude?: number | null;
  longitude?: number | null;
};

// 현장 문서(`SiteDocument`)는 목 저장소를 떠나 실제 API 타입으로 옮겼다 —
// `pages/sites/pmis/queries.ts` 의 `PmisSiteDocument`. 여기 남겨 두면 같은 이름의 타입이 둘이 된다.

// ── 주간회의 ─────────────────────────────────────────────────────────────────

export type TaskType = 'prev_week' | 'curr_week';

export type SiteTaskCategory =
  | '공정율'
  | '공사진행사항'
  | '외주'
  | '자재'
  | '설계변경'
  | '대갑기성'
  | '인원'
  | '착공'
  | '사용승인'
  | '기타';

/** 화면에 나오는 순서이자 정렬 기준. 배열 순서 자체가 의미를 가지므로 임의로 바꾸지 않는다. */
export const SITE_TASK_CATEGORIES: SiteTaskCategory[] = [
  '공정율',
  '공사진행사항',
  '외주',
  '자재',
  '설계변경',
  '대갑기성',
  '인원',
  '착공',
  '사용승인',
  '기타',
];

/** 하위 구분이 있는 분류. 없는 분류는 내용만 입력한다. */
export const SITE_TASK_SUBCATEGORIES: Partial<Record<SiteTaskCategory, string[]>> = {
  공사진행사항: ['토목', '조경', '건축', '기계', '전기', '소방', '안전', '품질'],
  외주: ['현설', '입찰', '계약'],
  자재: ['청구', '구매', '계약'],
  대갑기성: ['청구', '수금'],
};

/** 업체명 입력이 필요한 하위 구분. */
export const SUBCATEGORY_NEEDS_COMPANY = ['계약'];

export type HqTaskItem = {
  id: string;
  weekId: string;
  departmentId: string;
  taskType: TaskType;
  content: string;
  sortOrder: number;
  /**
   * 통합 달력에 올릴 날짜(선택). 비어 있으면 달력에 뜨지 않는다.
   *
   * 주간업무는 주차에 매달려 있고 날짜가 없었다 — 그래서 달력에 올릴 근거가 없었다. 이 값이 그 근거이며,
   * <b>주차가 주인이고 이 날짜는 표시용</b>이다. 날짜를 지워도 업무는 그 주차에 그대로 남는다.
   */
  scheduleDate: string | null;
};

/**
 * 현장 업무 한 건.
 *
 * `content` 는 분류에 따라 평문이거나 JSON 문자열이다 — v1이 컬럼을 늘리지 않고 payload 를 밀어 넣은 결과다.
 * 읽고 쓰는 규칙은 `pages/meetings/siteTaskContent.ts` 한 곳에만 둔다.
 */
export type SiteTaskItem = {
  id: string;
  weekId: string;
  siteId: string;
  departmentId: string | null;
  category: SiteTaskCategory;
  content: string;
  sortOrder: number;
  /**
   * 통합 달력에 올릴 날짜(선택). {@link HqTaskItem.scheduleDate} 와 같다.
   *
   * `공사진행사항` 은 예외다 — 이미 `content` 안에 시작일을 갖고 있어 화면이 그것을 폴백으로 읽는다.
   * 그래서 그 분류에는 날짜 칸을 따로 두지 않는다. 칸이 둘이면 어느 쪽이 달력에 뜨는지 알 수 없다.
   */
  scheduleDate: string | null;
};

/** 주차별 대표님 전달/지시사항. 본사·현장 각각 주차당 한 건이다. */
export type WeeklyExecutiveNote = {
  id: string;
  weekId: string;
  noteType: 'hq' | 'site';
  content: string;
  updatedAt: string;
};

// ── 사용자 관리 ──────────────────────────────────────────────────────────────

/**
 * 사용자가 볼 수 있는 부서·현장 범위(`auth_user_scopes`).
 *
 * ⚠ **인력배치표와는 다른 표다** (2026-09-01 바로잡음). 배치표가 그리는 것은
 * `hr_personnel_assignments` 이고, 여기에 현장을 붙여도 그쪽에는 줄이 생기지 않는다 —
 * 사람을 현장에 세우는 일은 전술판의 발령이 한다(`DutyPlacementSync` §지키는 선).
 * 예전 주석이 "인력배치표 노출 여부" 라고 적어 두어, 화면의 체크박스 이름까지 그 말을 따라갔다.
 */
export type UserScope = {
  scopeType: 'department' | 'site';
  scopeName: string;
  /** 대표 소속. 명함·마이페이지·직원찾기가 이 줄을 그 사람의 소속으로 적는다. */
  primary: boolean;
  /**
   * 마이페이지 소속 목록에 이 줄을 함께 보일지. **인력배치표는 이 값을 읽지 않는다** —
   * 이름이 `placement` 라서 오래 그렇게 읽혔다.
   */
  showInPlacement: boolean;
  /**
   * 이 소속에서 맡은 자리(`ref_duties.name` — 팀장·현장대리인·총괄소장…). 없으면 null.
   *
   * ★ **저장되는 칸이 아니다**(V215). 종전에는 계정에 따로 적혀 배치표와 사본 둘이 됐고, 두
   * 사본이 서로 덮어쓰기만 하고 「지우기」는 어느 방향으로도 가지 않아 여기서 해제한 직책이
   * 인력배치표에 그대로 남았다(사용자 지적 2026-09-03).
   *
   * 방향에 따라 뜻이 다르다.
   * - **읽을 때** — 배치표(`hr_personnel_assignments`)에서 조인해 온 표시값. 화면은 회색 글자다.
   * - **계정을 처음 만들 때** — 화면이 고른 값. 배치 제안으로 실려 전술판 승인을 지난다.
   *
   * 수정 저장에서는 서버가 무시한다 — 자리를 옮기고 내리는 일은 전술판(발령)이 한다.
   */
  duty: string | null;
};

/** 메뉴 하나에 대한 동작별 허용 여부. */
export type MenuPermission = {
  menuKey: string;
  canView: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
};

export type UserRecord = {
  id: string;
  email: string;
  name: string;
  rank: string | null;
  userType: UserType;
  active: boolean;
  mustChangePassword: boolean;
  scopes: UserScope[];
  /**
   * 아직 열지 않은 메뉴 중 이 사람에게 <b>미리 열어 준</b> 수(V264 · 2026-09-14).
   *
   * 미리보기는 <b>오픈 전에만 살아 있는</b> 임시 상태인데, 모달을 하나씩 열어 봐야 알 수 있으면
   * 시험이 끝난 뒤에도 아무도 걷지 않는다. 오픈하면 서버가 지우므로 이 수는 저절로 0 이 된다.
   */
  menuPreviewCount: number;
};

/**
 * 보미 실적 한 건. 현장 정보를 **복사해 둔 스냅샷**이다 — 현장 관리에서 이름이나 규모를 고쳐도
 * 이미 홍보에 쓴 실적은 그대로 남아야 하기 때문에 참조가 아니라 복사다(`sourceSiteId` 는 출처 표시용).
 */
export type BomiPerformancePost = {
  id: string;
  sourceSiteId: string | null;
  siteName: string;
  siteCategory: SiteCategory | null;
  status: SiteStatus | null;
  contractStartDate: string | null;
  contractEndDate: string | null;
  actualStartDate: string | null;
  scheduledCompletionDate: string | null;
  scale: string | null;
  contractAmount: string | null;
  floorCount: string | null;
  grossAreaSqm: number | null;
  facilityType: string | null;
  siteAddress: string | null;
  clientName: string | null;
  supervisorName: string | null;
  designerName: string | null;
  /** 적용 공법 설명. */
  methodText: string | null;
  journalPdfName: string | null;
  /**
   * **저장할 때만 쓰는 칸.** `POST /v1/performances/uploads` 로 받은 오브젝트 키다.
   * 파일을 새로 올린 저장 요청에만 싣는다 — 비워 보내면 서버가 저장된 키를 그대로 둔다.
   */
  journalPdfKey?: string | null;
  journalPdfUrl: string | null;
  /**
   * 첨부 — 글쓴이가 붙인 순서대로(V455). `journalPdfName`·`journalPdfUrl` 은 이 목록의 **첫 장 사본**이다.
   * 재시작 전 서버는 이 칸을 안 주므로 `?` 다 — 없으면 위 한 칸으로 읽는다.
   */
  attachments?: TechAttachment[];
  /**
   * **저장할 때만 쓰는 칸.** 「지금 이 실적의 첨부는 이 순서로 이것들」 — 서버는 목록을 통째로 갈아 끼운다.
   * 안 보내면 서버가 위 공사지 한 칸을 옛 규칙대로 읽는다(빈 배열은 「전부 뗐다」).
   */
  attachmentRefs?: TechAttachmentRef[];
  createdAt: string;
};

/** 보미기술집·보미실적 첨부 한 개(V455). 공지 첨부와 같은 모양이다. */
export type TechAttachment = {
  id: string;
  fileName: string | null;
  /** 내려받기 링크 — 올린 파일명 그대로 받아진다. 저장소에 파일이 없으면 null. */
  url: string | null;
  /** 미리보기 링크(inline). */
  previewUrl: string | null;
};

/** 저장 요청에 싣는 첨부 한 줄 — 방금 올린 파일(`storageKey`) 또는 이미 붙어 있던 첨부(`id`). */
export type TechAttachmentRef = {
  id?: string | null;
  storageKey?: string | null;
  fileName: string | null;
};

// ── 보미소식 ─────────────────────────────────────────────────────────────────

/**
 * 공지 분류. 배열 순서가 곧 화면의 탭 순서다 — 안전·기획·인사·정보(공통 항목)가 앞에 서고,
 * 계약·착공·진행·준공이 현장 진행 순서대로 뒤에 붙는다.
 *
 * 「안전」 은 **맨 앞**이다(사용자 지시 2026-09-03 · V222). 안전 공지는 읽어 두면 좋은 것이 아니라
 * 그날 지켜야 하는 것이라, 탭 줄에서 맨 먼저 눈에 닿아야 한다.
 *
 * 예전의 「공통」 한 칸은 기획·인사·정보 셋으로 갈렸다. 기존 글은 전부 정보(`info`)로 옮겼다(V64) —
 * 셋 중 어디인지는 글마다 다르고 본문으로 가를 수 없어, 기계로 나누면 틀린 분류가 조용히 박힌다.
 *
 * 「보미소식」 은 사람 소식(경조사·생일자)이다(V151). 처음에는 맨 뒤에 붙였다가 **인사 다음**으로
 * 옮겼다(사용자 지시 2026-08-28) — 뒤의 넷(계약·착공·진행·준공)은 현장 진행 순서라 그 줄을 끊지
 * 않으면서 사람 소식을 앞으로 당길 수 있는 자리가 거기다.
 */
export const NOTICE_CATEGORIES = [
  { value: 'safety', label: '안전' },
  { value: 'planning', label: '기획' },
  { value: 'hr', label: '인사' },
  { value: 'bomi', label: '보미소식' },
  { value: 'info', label: '정보' },
  { value: 'contract', label: '계약' },
  { value: 'starting', label: '착공' },
  { value: 'progress', label: '진행' },
  { value: 'completion', label: '준공' },
] as const;

export type NoticeCategory = (typeof NOTICE_CATEGORIES)[number]['value'];

export function noticeCategoryLabel(category: NoticeCategory): string {
  return NOTICE_CATEGORIES.find((option) => option.value === category)?.label ?? '정보';
}

/**
 * 공지가 어느 게시판 것인지. 자산 관련 공지는 보미소식이 아니라 자산관리 탭에서 본다.
 *
 * 글의 모양이 같아 표도 화면도 한 벌이고, 이 값과 메뉴 키(`news_notices`·`asset_notices`)만 갈린다.
 * 게시판은 등록할 때 정해지고 수정으로는 바뀌지 않는다 — 서버가 그렇게 막는다.
 */
export type NoticeBoardKey = 'news' | 'asset';

export type NoticePost = {
  id: string;
  title: string;
  content: string;
  authorName: string | null;
  boardKey: NoticeBoardKey;
  category: NoticeCategory;
  /**
   * 공지 날짜 `YYYY-MM-DD`(V405). 서버가 저장할 때 제목 앞에 `YYYY-MM-DD_` 로 붙인다.
   * 자동 생성 글·옛 글은 비어 있다.
   */
  noticeDate?: string | null;
  /** 이 공지를 낳은 사내지침(V429). 사내지침&매뉴얼에서 함께 올린 글만 채워진다. */
  guidelineId?: string | null;
  /**
   * 이 글의 첨부 — **붙인 순서 그대로**다. 여러 개가 붙는다.
   *
   * 아래 `attachmentName`·`attachmentUrl` 은 이 목록의 **첫 장**이다. 서버가 둘을 함께 채워 주는
   * 이유는 이미 배포된 앱 번들이 그 둘을 읽기 때문이다 — 새 화면은 이 목록을 읽는다.
   */
  attachments?: NoticeAttachment[];
  /**
   * **저장할 때만 쓰는 칸.** 「지금 이 글의 첨부는 이 순서로 이것들」 이라는 완성된 상태를 보낸다.
   * 서버는 목록을 통째로 갈아 끼운다 — 빠진 것은 빠지고, 배열 순서가 곧 화면 순서다.
   *
   * **안 보내면(undefined) 서버가 아래 한 칸짜리 첨부를 읽는다.** 빈 배열과 다른 뜻이다:
   * 빈 배열은 「첨부를 전부 뗐다」 이고, 안 보낸 것은 「이 칸을 모르는 화면」 이다.
   */
  attachmentRefs?: NoticeAttachmentRef[];
  /** 첫 번째 첨부의 이름. **옛 화면을 위한 사본**이다 — 새 화면은 `attachments` 를 읽는다. */
  attachmentName: string | null;
  /**
   * 첫 번째 첨부의 링크. v1은 Supabase 공개 버킷의 public URL 을 그대로 저장했다 —
   * 링크만 알면 누구나 받을 수 있었다는 뜻이다. v2 는 만료되는 presigned URL 을 전제로 하며,
   * 그래서 이 값은 **저장된 주소가 아니라 그때그때 발급받는 주소**로 바뀐다.
   */
  attachmentUrl: string | null;
  /**
   * **저장할 때만 쓰는 칸.** `POST /v1/board-attachments/confirm` 이 준 오브젝트 키다.
   * 첨부 목록을 모르는 화면이 쓰던 자리라, `attachmentRefs` 를 보내면 서버가 이 칸을 보지 않는다.
   */
  attachmentKey?: string | null;
  /**
   * 달린 댓글 수. 목록에서 펼치지 않고도 말이 오갔는지 보이라고 서버가 함께 세어 준다.
   * 목 자료로 물러선 화면에는 없을 수 있어 `?` 다.
   */
  commentCount?: number;
  /**
   * **나에게 새 글인가** — 연 적이 없고 발행 뒤 며칠 안. 서버가 판정한다(글마다 읽음을 적는다).
   * 목록은 이 글에 New 를 달고 맨 위로 올리며, 펼치면 `POST /v1/notices/{id}/read` 로 끈다.
   */
  isNew?: boolean;
  /**
   * 글쓴이 계정. 보미소식 공지는 본인과 관리자에게만 [수정]·[삭제] 가 선다(사용자 지시 2026-10-22).
   * 옛 글·재시작 전 서버에는 없다 — 그때는 관리자만 본다.
   */
  createdBy?: string | null;
  /**
   * 이 글에 **댓글을 달 수 있는가**. 쓰는 사람이 글마다 정한다 — 전에는 보미소식 분류면 열리고
   * 아니면 닫혔는데, 실제로는 같은 안전 공지도 의견을 받을 것과 통보로 끝낼 것이 갈린다.
   *
   * 꺼져 있어도 **이미 달린 댓글은 보인다**. 닫는 것은 「더 받지 않는다」 이지 「지운다」 가 아니다.
   */
  commentsEnabled?: boolean;
  /**
   * 그 댓글이 **익명일 수 있는가**. 익명으로 고정하는 것이 아니라 **고를 수 있게** 하는 값이다 —
   * 이름을 걸고 쓰는 사람과 감추고 싶은 사람이 한 글에 함께 있다.
   */
  commentsAnonymous?: boolean;
  /**
   * 홈 「보미소식」 위젯에 세워 둘 일수 — 7·14·30 중 하나이거나 비어 있다.
   *
   * **비어 있으면 일주일이다.** 소식마다 유효기간이 다르고(부고는 발인까지, 결혼은 몇 주),
   * 그 판단은 글을 쓰는 사람이 안다. 보미소식 분류 글에만 쓴다.
   */
  widgetDays?: number | null;
  /**
   * 이 소식의 **대상자** — 누구의 경조사인가.
   *
   * 이름·소속은 **작성 당시의 표기**다. 인력 id 로 매번 조인해 지금 소속을 붙이면 3월에 올린
   * 부고에 8월의 부서가 달린다 — 그건 그때의 기록이 아니다(`authorName` 과 같은 규칙).
   * 특정인의 일이 아닌 소식이면 비어 있고, 그때 화면은 제목만 세운다.
   */
  subjectPersonnelId?: string | null;
  subjectName?: string | null;
  subjectDeptSite?: string | null;
  /**
   * 본문 **아래**에 서는 사진 앨범. 글쓴이가 정한 순서 그대로다.
   *
   * 첨부파일(`attachmentName`)과 다른 자리다 — 저쪽은 「내려받을 문서」 이고 이쪽은 「보이는 사진」.
   * 저장할 때는 `storageKey` 를 담아 보내고(`NoticeImageRef`), 응답에는 만료되는 `url` 이 온다.
   */
  images?: NoticeImage[];
  /**
   * **저장할 때만 쓰는 칸.** 「지금 이 글의 사진은 이 순서로 이것들」 이라는 완성된 상태를 보낸다.
   * 서버는 목록을 통째로 갈아 끼운다 — 빠진 것은 빠지고, 배열 순서가 곧 앨범 순서다.
   */
  imageRefs?: NoticeImageRef[];
  /**
   * **저장할 때만 쓰는 칸 셋** — 이 공지를 어디로 더 알릴지. 셋 다 **기본은 꺼짐**이고,
   * **등록**에만 뜻이 있다(수정에서는 서버가 보지 않는다 — 오타 하나 고칠 때마다 전 직원에게
   * 다시 나가면 아무도 공지를 고치지 않는다).
   *
   * - `pushNotify` — 앱 푸시. 그 순간 울리고 사라진다(제목까지만 실린다)
   * - `todoNotify` — 전 직원 할일 한 줄. **체크할 때까지 남는다**
   * - `mailNotify` — 전 직원 메일. **본문이 실려** 시스템에 들어오지 않아도 읽힌다
   *
   * 응답에는 실려 오지 않는다 — 보낸 사실은 글의 속성이 아니라 그때 한 번의 발송이다.
   */
  pushNotify?: boolean;
  todoNotify?: boolean;
  mailNotify?: boolean;
  /** 카카오 알림톡(V416). 본인이 인증한 휴대폰이 있는 직원에게만 간다. 템플릿코드가 비어 있으면 쌓이지 않는다. */
  alimtalkNotify?: boolean;
  /**
   * **저장할 때만 쓰는 칸.** 홈에 들어온 사람 앞에 **창으로** 며칠 세울지 — 쓰는 사람이 적은
   * 숫자다(1~90). 비어 있으면 팝업을 켜지 않은 것이다.
   *
   * 위의 셋과 달리 기간이 딸린다. 팝업은 화면을 막고 서는 자리라, 기한이 없으면 새로 입사한
   * 사람 앞에 몇 달 전 공지가 뜬다 — 사람마다 「더이상 보지 않기」 를 누르는 것으로는 못 막는다.
   */
  popupDays?: number | null;
  /** 전 직원에게 나간 시각(V217). 초안은 null — 목록의 날짜 정렬이 `createdAt` 보다 먼저 본다. */
  publishedAt?: string | null;
  createdAt: string;
};

/**
 * 응답에 실려 오는 첨부 한 개. `url` 은 만료되는 **내려받기** 링크, `previewUrl` 은 같은 파일의
 * **미리보기** 링크다. 둘이 갈린 이유는 `NoticeAttachmentList` 에 적었다.
 */
export type NoticeAttachment = {
  id: string;
  fileName: string | null;
  url: string | null;
  previewUrl: string | null;
};

/**
 * 저장 요청에 싣는 첨부 한 개 — **둘 중 하나**를 가리킨다.
 *
 * - `storageKey` — 방금 올린 파일, 또는 자료실에서 끌어다 놓은 자료(`confirm` 이 준 키)
 * - `id` — 이미 이 글에 붙어 있던 첨부. **그대로 둔다**는 뜻이다
 *
 * 옛 첨부를 id 로 가리키는 이유는 사진과 같다: 응답에 오브젝트 키가 나오지 않아(만료되는 `url` 만
 * 온다) 화면이 키를 되돌려 보낼 방법이 없다. 서버가 자기 표에서 찾는다.
 */
export type NoticeAttachmentRef = {
  id?: string | null;
  storageKey?: string | null;
  fileName: string | null;
};

/** 응답에 실려 오는 사진 한 장. `url` 은 만료되는 presigned URL 이다. */
export type NoticeImage = {
  id: string;
  fileName: string | null;
  url: string | null;
  /**
   * 보일 최대 폭 — 320(작게) · 560(보통) · 900(크게). 없으면 제한 없음(카드 폭까지).
   *
   * **상한이지 폭이 아니다.** 작은 사진은 늘리지 않는다 — 400px 짜리를 900px 로 키우는 것은
   * 크기를 정한 것이 아니라 화질을 버린 것이다.
   */
  maxWidthPx?: number | null;
};

/**
 * 저장 요청에 싣는 사진 한 장 — **둘 중 하나**를 가리킨다.
 *
 * - `storageKey` — 방금 올린 새 사진(자료실 `confirm` 이 준 키)
 * - `id` — 이미 이 글에 붙어 있던 사진. **그대로 둔다**는 뜻이다
 *
 * 옛 사진을 id 로 가리키는 이유: 응답에는 오브젝트 키가 나오지 않아(만료되는 `url` 만 온다)
 * 화면이 키를 되돌려 보낼 방법이 없다. 서버가 자기 표에서 찾는다.
 */
export type NoticeImageRef = {
  id?: string | null;
  storageKey?: string | null;
  fileName: string | null;
  maxWidthPx?: number | null;
};

/**
 * 공지 댓글 한 줄 — 보미소식(경조사·생일자)에 남기는 말.
 *
 * `mine` 은 **서버가 판정해서 준다.** 작성자 id 를 내려 화면이 스스로 비교하게 두면, 지울 수 있는지의
 * 기준이 서버와 화면 두 곳에 생기고 둘은 언젠가 어긋난다.
 */
export type NoticeComment = {
  id: string;
  /**
   * 작성자 이름. **익명 댓글이면 비어 있다** — 서버가 이름·소속·사진·인력 id 를 전부 비워 보낸다.
   * 화면이 감추는 것이 아니라 **오지 않는다**(익명이 지켜지는 자리는 응답이다).
   */
  authorName: string | null;
  /**
   * 작성자의 인력 id. 이름을 누르면 이 값으로 직원찾기 명함이 열린다.
   * 계정이 인력과 연결돼 있지 않으면 없고, 그때는 이름으로 조직도를 좁혀 연다.
   */
  authorPersonnelId: string | null;
  /**
   * 작성자의 **지금** 소속. 저장된 사본이 아니라 서버가 매번 조인해 붙인다 — 부서를 옮긴 사람의
   * 댓글에 옛 부서가 달리면 기록이 아니라 틀린 정보다. 계정이 인력과 연결돼 있지 않으면 없다.
   */
  deptSite: string | null;
  /** 증명사진의 **만료되는** 링크. 응답마다 새로 발급된다(공개 버킷을 만들지 않는다). */
  photoUrl: string | null;
  content: string;
  mine: boolean;
  /** 이름을 감추고 남긴 댓글인가. 위의 이름·소속·사진은 그때 전부 비어 있다. */
  anonymous: boolean;
  /** 고친 적이 있는가. 서버가 등록·수정 시각을 견줘 판정한다. */
  edited: boolean;
  createdAt: string;
  /** 이 댓글에 달린 답글. **한 겹까지**라 답글의 이 칸은 언제나 빈 배열이다. */
  replies: NoticeComment[];
};

/** 입/퇴사자 소식 한 건. `newsDate` 의 연·월로 화면의 월 묶음이 정해진다. */
export type EmployeeNewsItem = {
  id: string;
  newsType: 'join' | 'leave';
  employeeName: string;
  rank: string | null;
  /** 본사 부서명 또는 현장명. */
  deptSite: string | null;
  newsDate: string;
  email: string | null;
  /** 퇴사 소식의 입사일(근속기간용, DOC-50 §7.2). 비우면 서버가 이메일로 이어진 인사 명부에서 채운다. */
  joinDate?: string | null;
  /** 근속 개월. **서버가 계산해 내려주는 값**이라 저장하지 않는다 — 보내도 무시된다. */
  tenureMonths?: number | null;
};

export const TREND_TYPES = [
  { value: 'news', label: '건설 뉴스' },
  { value: 'journal', label: '학회·저널' },
  { value: 'market', label: '시장 동향' },
  { value: 'other', label: '기타' },
] as const;

export type TrendType = (typeof TREND_TYPES)[number]['value'];

/**
 * 건설동향 글. `draft` 는 수집기가 가져온 초안이고, 담당자가 확인하면 `published` 가 된다.
 * 초안 단계를 두는 이유는 외부 피드에서 온 글이 사내에 그대로 노출되지 않게 하기 위해서다.
 */
export type ConstructionTrendPost = {
  id: string;
  title: string;
  trendType: TrendType;
  content: string;
  sourceName: string | null;
  sourceUrl: string | null;
  publishedAt: string | null;
  createdAt: string;
};

/** 수집 대상 RSS·피드. */
export type ConstructionTrendSource = {
  id: string;
  name: string;
  feedUrl: string;
  trendType: TrendType;
  active: boolean;
  sortOrder: number;
};

/**
 * 사내지침 글. 같은 지침의 개정본은 `versionGroupKey` 로 묶이고 화면에서 버전을 골라 본다.
 * 첨부는 여러 개다 — v1은 `attachment_name`·`attachment_url` 한 칸에 JSON 배열을 밀어 넣어
 * 문자열일 때와 배열일 때를 매번 다시 해석했다. v2 DTO 는 처음부터 배열로 받는다.
 */
export type InternalGuidelinePost = {
  id: string;
  title: string;
  guidelineType: string;
  departmentName: string;
  effectiveStartDate: string;
  effectiveEndDate: string | null;
  /** 개정일 — 이 판을 고친 날. 시행일과 다를 수 있다(12월에 고쳐 1월 1일 시행). */
  revisedAt: string | null;
  /** 이 판에서 바뀐 내용. 본문 diff 가 아니라 사람이 쓴 요약이다. */
  changeNote: string | null;
  content: string;
  /**
   * 첨부 목록. **순서가 곧 화면 순서**이고, 저장할 때 여기 없는 첨부는 지운 것으로 본다.
   *
   * 응답과 저장 요청이 같은 배열을 쓰되 채워지는 칸이 다르다 — 응답은 `url`·`sortOrder`,
   * 저장은 `objectKey`(방금 올린 파일) 또는 `savedSortOrder`(그대로 두는 첨부)다.
   * 이름으로 맞추지 않는 이유: 첨부 이름의 오탈자만 고쳐도 이미 올라간 파일이 끊긴다.
   */
  attachments: Array<{
    name: string;
    /** 응답 전용. 만료되는 presigned URL. 파일이 아직 없으면 null. */
    url?: string | null;
    /** 응답 전용. 저장된 순번. */
    sortOrder?: number;
    /** 저장 전용. `POST /v1/guidelines/uploads` 로 받은 오브젝트 키. */
    objectKey?: string;
    /** 저장 전용. 그대로 두는 첨부가 저장돼 있던 순번 — 서버가 이 값으로 파일을 다시 찾는다. */
    savedSortOrder?: number;
  }>;
  externalUrlName: string | null;
  externalUrl: string | null;
  /** 같은 지침의 개정본을 묶는 키. 비어 있으면 자기 자신이 하나의 묶음이다. */
  versionGroupKey: string | null;
  versionLabel: string | null;
  /**
   * 이 지침으로 하는 신청의 <b>결재 서식 키</b>(V183 · 요청 No.6·7).
   *
   * <p>지침을 읽고 나면 창을 닫고 전자결재에서 서식을 다시 찾아야 했다. 이 값이 있으면 상세에
   * 「이 지침으로 신청하기 →」 버튼이 선다. <b>비어 있는 것이 정상</b>이다 — 신청과 짝이 없는
   * 지침이 대부분이고, 그때는 버튼을 안 그린다.
   */
  approvalFormKey: string | null;
  keywords: string[];
  createdAt: string;
  /** 게시물을 마지막으로 손댄 때. 개정일과 다른 축이다 — 오탈자 하나만 고쳐도 움직인다. */
  updatedAt: string | null;
};

// ── 보미기술집 ───────────────────────────────────────────────────────────────

export const TECH_WORK_TYPES = ['건축', '구조', '기계', '전기', '통신', '토목', '조경', '기타'] as const;
export type TechWorkType = (typeof TECH_WORK_TYPES)[number];

export const TECH_CASE_TYPES = ['우수사례', '미흡사례', 'VE개선사례', '기타'] as const;
export type TechCaseType = (typeof TECH_CASE_TYPES)[number];

/**
 * 기술 자료 한 건. 공종별 기술집(`case`)과 신기술 소개(`new`)가 같은 모양을 쓰되
 * 사례 종류(`caseType`)는 공종별 기술집에만 있다.
 *
 * 첨부 파일은 여러 개다(`attachments`, V455). `attachmentName`·`attachmentUrl` 은 **웹 링크** 한 칸이다 —
 * 옛 글이 쓰던 구글 드라이브 링크라 남겨 두었다.
 */
export type TechnicalPost = {
  id: string;
  /**
   * 어느 게시판 글인지. **등록·수정·삭제 요청이 전부 이 값을 함께 보내야 한다** —
   * 서버가 게시판별로 권한을 보기 때문이다(`?boardKind=`).
   */
  boardKind: 'case' | 'new';
  workType: TechWorkType;
  siteName: string | null;
  caseType: TechCaseType | null;
  content: string;
  attachmentName: string | null;
  attachmentUrl: string | null;
  /** 첨부 파일 — 글쓴이가 붙인 순서대로. 재시작 전 서버는 이 칸을 안 주므로 `?` 다. */
  attachments?: TechAttachment[];
  /**
   * **저장할 때만 쓰는 칸.** 「지금 이 글의 첨부 파일은 이 순서로 이것들」 — 서버는 목록을 통째로 갈아 끼운다.
   * 안 보내면 서버가 저장된 파일을 건드리지 않는다(빈 배열은 「전부 뗐다」).
   */
  attachmentRefs?: TechAttachmentRef[];
  /** 작성자가 삭제를 요청한 상태. 실제 삭제는 권한 있는 사용자가 한다. */
  deleteRequested: boolean;
  createdAt: string;
};

// ── 월간회의 ─────────────────────────────────────────────────────────────────

/** `hq` 는 카테고리로, `site`·`field` 는 현장으로 일정을 구분한다. */
export type MonthlyMeetingScope = 'hq' | 'site' | 'field';

export type MonthlyMeetingRecurrenceType = 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly';

export type MonthlyMeetingCategory = {
  id: string;
  name: string;
  color: string;
  sortOrder: number;
};

/**
 * 반복 규칙을 품은 일정 원본. 달력에 그려지는 개별 일정은 여기서 펼쳐 만든다
 * (`pages/meetings/monthly/recurrence.ts`).
 *
 * v1은 관계부서를 `monthly_meeting_event_departments` 조인 테이블에 따로 두어,
 * 저장할 때마다 전부 지우고 다시 넣었다(`replaceEventDepartments`). 화면이 필요로 하는 것은
 * "이 일정의 부서 목록" 하나뿐이므로 v2 DTO 는 배열로 받는다 — 조인은 백엔드 안에 남는다.
 */
export type MonthlyMeetingEvent = {
  id: string;
  scope: MonthlyMeetingScope;
  title: string;
  eventDate: string;
  eventTime: string | null;
  /** 끝나는 시각(V123). 회의실을 잡을 때 예약 구간의 끝이다 — 없으면 서버가 한 시간으로 본다. */
  eventEndTime: string | null;
  categoryId: string | null;
  siteId: string | null;
  notes: string | null;
  /**
   * 장소. 회의실을 고르지 **않은** 일정에서 사람이 적는 값이다. 회의실을 골랐으면 화면이 그 방
   * 이름을 여기 함께 적어 둔다 — 예약이 취소돼도 「어디였는지」 는 남아야 한다.
   */
  location: string | null;
  /**
   * 회의실 자산(`asset_items.asset_type='ROOM'`). 값이 있으면 저장이 회의실 예약을 **함께 잡는다**
   * (V123). 반복 일정은 그 회차만 잡힌다 — 예약은 구체적인 구간이라 무한 반복을 담을 수 없다.
   */
  roomAssetId: string | null;
  /** 참석자에게 TODO·메일 통보를 보낼지. 기본 켬. */
  notifyAttendees: boolean;
  /** 시작 전 앱 푸시 · 카카오 알림톡과 그 「몇 분 전」(V417). notifyAttendees 는 하루 전 할일이다. */
  notifyPush?: boolean;
  notifyAlimtalk?: boolean;
  remindMinutesBefore?: number | null;
  /** 그룹(팀) 단위 참석 대상. 화면의 '관계 부서' 가 곧 이것이다 — 통보가 이 목록으로 나간다. */
  departmentIds: string[];
  /** 개인 참석자. 계정 id 가 아니라 **인력 id**(주소록에서 고른 값)다. */
  attendeePersonnelIds: string[];
  recurrenceType: MonthlyMeetingRecurrenceType;
  recurrenceInterval: number;
  /** `daily` 반복에서 어느 요일에만 잡을지. 0=일요일. */
  recurrenceWeekdays: number[] | null;
  recurrenceUntil: string | null;
};

/** 반복 일정 중 하루만 지우거나 고친 기록. */
export type MonthlyMeetingException = {
  id: string;
  eventId: string;
  occurrenceDate: string;
  actionType: 'deleted' | 'updated';
  overrideTitle: string | null;
  overrideCategoryId: string | null;
  overrideSiteId: string | null;
  overrideNotes: string | null;
  overrideDepartmentIds: string[] | null;
};

/** 달력 한 칸에 그려지는 일정. 저장되지 않고 매번 계산된다. */
export type MonthlyMeetingOccurrence = {
  /** `이벤트id:날짜`. 저장된 식별자가 아니다. */
  id: string;
  sourceEventId: string;
  scope: MonthlyMeetingScope;
  occurrenceDate: string;
  eventTime: string | null;
  eventEndTime: string | null;
  title: string;
  categoryId: string | null;
  siteId: string | null;
  notes: string | null;
  location: string | null;
  roomAssetId: string | null;
  notifyAttendees: boolean;
  /** 시작 전 앱 푸시 · 카카오 알림톡과 그 「몇 분 전」(V417). notifyAttendees 는 하루 전 할일이다. */
  notifyPush?: boolean;
  notifyAlimtalk?: boolean;
  remindMinutesBefore?: number | null;
  departmentIds: string[];
  attendeePersonnelIds: string[];
  recurrenceType: MonthlyMeetingRecurrenceType;
  recurrenceInterval: number;
  recurrenceWeekdays: number[] | null;
  recurrenceUntil: string | null;
  isRecurring: boolean;
};

// ── 통합 캘린더 ──────────────────────────────────────────────────────────────

/**
 * 통합 캘린더에 얹히는 연차 한 건(`GET /api/v1/calendar/leaves`).
 *
 * 사유는 담기지 않는다 — 달력이 필요로 하는 것은 "이 사람이 이 날 자리에 없다" 뿐이다.
 * `scope` 는 이 행이 열린 근거이며, 화면은 `self`(내 연차)를 다르게 그리는 데만 쓴다.
 * 무엇이 내려올지는 서버가 정한다(본인 · 같은 소속 · 인사자료 권한이면 전원).
 */
export type CalendarLeave = {
  id: string;
  personnelId: string;
  personName: string;
  rank: string | null;
  /** 본사 부서명 또는 현장명. */
  deptSite: string | null;
  startDate: string;
  endDate: string;
  days: number;
  /** 연차 / 반차 / 특별 등. */
  leaveType: string;
  scope: 'self' | 'team' | 'all';
};

/** 본사 구내식당 주간 식단. `menuText` 는 월~일 7줄이다. */
export type HqCafeteriaMenu = {
  id: string;
  weekStart: string;
  menuText: string;
  updatedAt: string;
};

/**
 * 현장별 구내식당 주간 식단. 본사와 같은 형식(`menuText` 월~일 7줄)이되 현장마다 한 건이다.
 * 현장이 여럿이라 `siteId` 로 가른다.
 */
export type SiteCafeteriaMenu = {
  id: string;
  siteId: string;
  weekStart: string;
  menuText: string;
  updatedAt: string;
};

export type SiteRestaurant = {
  id: string;
  siteId: string;
  restaurantName: string;
  address: string | null;
  menuText: string | null;
  priceText: string | null;
  sortOrder: number;
  createdAt: string;
};

/** 개선요청 처리 상태 — 「할 것인가」. `completed`(했는가)와 다른 축이다(V170 · 요청 #62). */
export type RequestFeasibility = 'PENDING' | 'ACCEPTED' | 'REVIEWING' | 'REJECTED';

export type RequestBoardPost = {
  id: string;
  /** 요청자. **전산 담당이 아니면 서버가 「익명」으로 바꿔 내려준다**(`RequesterVisibility`) */
  requesterName: string | null;
  /**
   * 내가 올린 글인가. 목록이 온통 「익명」이라 내 줄을 찾는 표시가 따로 필요하다.
   *
   * **서버가 보는 사람마다 계산해 붙이는 값**이라 등록 payload(`dbInsert`)에는 없다 — 그래서
   * optional 이다. 필수로 두면 글을 쓰는 자리에서 이 칸을 지어내야 하고, 지어낸 값은 무시된다.
   */
  mine?: boolean;
  requestContent: string;
  responseContent: string | null;
  completed: boolean;
  /** 반영 가능 여부. 담당자가 답할 때 정한다 */
  feasibility: RequestFeasibility;
  /** 예상 소요기간의 개략 표기(「2~4주」). 없으면 null */
  estimatedPeriod: string | null;
  createdAt: string;
  updatedAt: string;
};

export type Personnel = {
  id: string;
  /** 사번. 인사자료 엑셀과 인력 등록·개인정보 수정 창이 적는다. */
  employeeNo?: string | null;
  /** 신분(V413) — 정규직·정규직(수습)·계약직·계약직(수습)·계약직(기타)·실습생. 비어 있으면 모른다. */
  employmentStatus?: string | null;
  /**
   * 담당업무(V437). 자유 글 한 칸 — 본인(마이페이지)과 인사 담당(여기)이 같은 칸을 고친다.
   * 저장 요청에서 null 은 「건드리지 않음」, 빈 글자는 「지움」이다.
   */
  duties?: string | null;
  name: string;
  birthDate: string | null;
  phone: string | null;
  extension: string | null;
  email: string | null;
  deptSite: string | null;
  rank: string | null;
  position: string | null;
  field: string | null;
  /**
   * 최종학력 — 고졸 · 초대졸 · 대졸 · 석사 · 박사 (V93).
   *
   * 승진 소요기간이 학력마다 다른 칸이 있어서 필요하다(사원→주임). **비어 있는 것이 정상**이고,
   * 그때는 학력 조건이 붙은 기준 줄로 판정하지 않는다 — 짐작해서 채우면 진급 예정일자가 바뀐다.
   */
  education: string | null;
  /**
   * 군필 여부 (V570). 사원→주임 승진 기간이 군필자는 한 해 짧다. 비어 있으면 미필과 같이 판정한다.
   * 재시작 전 서버는 안 보낸다.
   */
  militaryServed?: boolean | null;
  joinDate: string | null;
  leaveDate: string | null;
  grade: string | null;
  score: number | null;
  /**
   * 자리위치 — 층 · 구역 · 좌석번호. 본사에서 사람을 찾아가는 데 전화번호보다 자주 쓰인다.
   * 현장 인원은 대개 비어 있다(어느 현장인지는 배치표가 답한다).
   */
  seatFloor: string | null;
  seatZone: string | null;
  seatNo: string | null;
  /**
   * 켜면 직원찾기·조직도에 서지 않는다. 실직원이 아닌 관리·시스템 계정용 행에 쓴다 —
   * 주소록은 인사 명부를 읽으므로, 명부에 행이 있으면 계정 성격이든 사람으로 선다.
   * 자료(연차·급여·자격증)는 그대로 남는다.
   */
  directoryHidden: boolean;
  /**
   * 증명사진 링크(만료되는 presigned URL). 저장소가 없거나 사진을 올리지 않았으면 null 이다.
   *
   * 선택 필드로 둔 이유: 편집 폼이 `Omit<Personnel, 'id'>` 를 그대로 저장 요청에 싣는데,
   * 사진은 저장 경로가 따로다(`PUT /personnel/{id}/photo`). 필수로 만들면 폼이 링크를
   * 되돌려 보내게 되고, 그 값은 서버가 쓰지도 않는다.
   */
  photoUrl?: string | null;
  /**
   * 로그인 계정이 이어져 있는지(`hr_personnel.user_id`). 서버가 계산해 내려주는 읽기 전용 값이라
   * 편집 폼은 이 값을 쓰지 않는다. 연결은 이메일이 같은 계정을 찾아 자동으로 된다.
   */
  accountLinked?: boolean;
};

/**
 * 인력의 소속. 한 사람이 본사 부서와 여러 현장에 동시에 걸릴 수 있다.
 *
 * v1은 이 표가 비어 있을 때를 대비해 `personnel.notes` 안의 JSON(`_structured.concurrent_sites`)에서
 * 소속을 되살리는 폴백을 브라우저에서 돌렸다(`lib/personnelAssignments.ts`, 165줄).
 * 마이그레이션이 끝나기 전의 임시 장치였고, v2 는 백엔드가 정리된 목록을 내려주는 것을 전제로
 * 폴백을 옮기지 않았다.
 */
export type PersonnelAssignment = {
  id: string;
  personnelId: string;
  assignmentType: 'department' | 'site';
  /** 부서명 또는 현장명. id 가 아니라 이름으로 묶는 것이 v1 스키마다. */
  assignmentName: string;
  teamLead: boolean;
  roleTitle: string | null;
  fieldName: string | null;
  sortOrder: number;
  /**
   * 본직·겸직·파견(V66). 소속 종류(`assignmentType`)와 <b>다른 축</b>이다 — 한 사람이 본사 부서에
   * 본직으로 있으면서 현장에 파견될 수 있다. 옛 배치는 전부 `primary` 로 읽힌다.
   */
  assignmentRole: AssignmentRole;
  /** 발령일. 손으로 넣은 옛 배치는 null. */
  startDate: string | null;
  /** 겸직·파견 종료 예정일. 본직은 null. */
  endDate: string | null;
  /** 이 배치로 수당 대상이 되는가. 금액은 없다 — 대상 여부만 개인 대시보드로 흐른다. */
  allowanceEligible: boolean;
  /** 고용 구분(V68). 구분을 안 적은 옛 배치는 `REGULAR` 로 온다. */
  employmentType: EmploymentType;
  /**
   * 그 현장의 <b>계약상 현장대리인</b>인가 (V401). 담당(`roleTitle`)과 다른 축이다 — 소장이
   * 겸하기도 하고, 담당이 「공무」 인 사람이 신고돼 있기도 하다. 한 현장에 한 명이고, 조직도에서
   * 「현장대리인」 칸에 세우면 서버가 맞춘다.
   */
  siteAgent: boolean;
  /**
   * 조직도 공무 자리의 「연락처 공개」 (V418). 그 현장 조직도의 공무 묶음에 서 있지 않으면 null —
   * 그때 연락처는 담당 글자로 가른다. 재시작 전 서버는 이 칸을 안 보내서 없을 수도 있다.
   */
  contactPublic?: boolean | null;
};

export type AssignmentRole = 'primary' | 'concurrent' | 'dispatch';

/**
 * 배치 성격의 이름. **소속 종류에 따라 부르는 말이 다르다** — 같은 `primary` 를 본사에서는 「본직」,
 * 현장에서는 「주현장」 이라 부른다(V68). 값을 나누지 않은 이유는 서버 `AssignmentRole` 주석에 있다.
 *
 * 소속 종류를 모르는 자리(개인 대시보드의 발령 이력 등)는 이 표를 쓴다.
 */
export const ASSIGNMENT_ROLE_LABELS: Record<AssignmentRole, string> = {
  primary: '본직',
  concurrent: '겸직',
  dispatch: '파견',
};

/** 현장에서 부르는 이름. 옛 부현장은 겸직으로 합쳤다(V441). */
export function assignmentRoleLabel(role: AssignmentRole, type: 'department' | 'site'): string {
  if (role === 'primary') return type === 'site' ? '주현장' : '본직';
  return ASSIGNMENT_ROLE_LABELS[role];
}

/**
 * 고용 구분(V68) — <b>어떤 신분으로 그 자리에 있는가</b>.
 *
 * 배치 성격(주현장·겸직·파견)과도, 맡은 일(공무·품질·BIM·현장대리인)과도 다른 축이다.
 * 「겸직 현장에서 BIM 을 맡은 PJ직」 이 정상적인 한 사람이고, 셋이 각각 다른 칸에 적힌다.
 */
export type EmploymentType = 'REGULAR' | 'OFFICE_ASSIST' | 'HIGHSCHOOL' | 'PROJECT';

export const EMPLOYMENT_TYPE_LABELS: Record<EmploymentType, string> = {
  REGULAR: '정규',
  OFFICE_ASSIST: '사무보조',
  HIGHSCHOOL: '고졸',
  PROJECT: 'PJ직',
};

/**
 * 인사발령 한 건 — <b>사람 하나를 어디서 어디로 옮겼는가</b>.
 *
 * 배치(`PersonnelAssignment`)가 "지금 어디 있는가" 라면 이쪽은 "언제 왜 그렇게 됐는가" 다.
 * 인력배치표의 전술판이 확정할 때 만들어지고, 개인 대시보드가 자기 것만 다시 읽는다.
 */
export type PersonnelOrder = {
  id: string;
  /** 한 번의 「발령 확정」 묶음. 발령 문서 한 장에 해당한다. */
  batchId: string;
  personnelId: string;
  /** 발령 당시 이름(스냅샷). 그 뒤 개명해도 그때의 문서는 그대로다. */
  personnelName: string;
  orderType: OrderType;
  assignmentType: 'department' | 'site';
  fromName: string | null;
  toName: string | null;
  roleTitle: string | null;
  fieldName: string | null;
  teamLead: boolean;
  employmentType: EmploymentType;
  effectiveDate: string;
  endDate: string | null;
  allowanceEligible: boolean;
  allowanceNote: string | null;
  reason: string | null;
  /** SCHEDULED = 예정. 발령일이 아직 오지 않았다 — 그날 00시에 배치가 된다(V247). */
  status: 'SCHEDULED' | 'CONFIRMED' | 'CANCELLED';
  /**
   * 이 발령이 옮기거나 풀 <b>대상</b> 배치. 전술판이 예정 해제를 그릴 때 「어느 카드가 그날
   * 사라지는가」 를 이 값으로 찾는다 — 해제는 도착지가 없어 이것 말고는 가리킬 것이 없다.
   */
  sourceAssignmentId: string | null;
  orderedByName: string | null;
};

export type OrderType = 'TRANSFER' | 'CONCURRENT' | 'DISPATCH' | 'RELEASE';

export const ORDER_TYPE_LABELS: Record<OrderType, string> = {
  TRANSFER: '전보',
  CONCURRENT: '겸직',
  DISPATCH: '파견',
  RELEASE: '해제',
};

/**
 * <b>배치 제안</b> 한 줄 — "이 계정에 «이 소속»이 붙었습니다. 배치할까요?"(V189).
 *
 * 환경설정 › 사용자에서 소속을 붙여도 인력배치표는 비어 있었다. 두 화면이 다른 표를 읽기 때문이다
 * (`UserScope` 주석). 계정 쪽에서 배치 줄을 만들지 않는 것은 규율이지만, 그 대가로 담당자가 같은
 * 사실을 두 번 적고 있었다 — 제안은 그 <b>입력만</b> 없앤다. 승인하면 여느 발령과 똑같은 줄이
 * 대장에 남는다.
 *
 * ⚠ 이것은 아직 아무것도 아니다. 이 줄이 있어도 배치도 권한도 움직이지 않는다.
 */
export type PlacementSuggestion = {
  id: string;
  userId: string;
  /** 이어진 인력. null 이면 승인이 인사자료까지 만든다(`createsPersonnel`). */
  personnelId: string | null;
  personName: string;
  rankName: string | null;
  email: string | null;
  scopeType: 'department' | 'site';
  scopeName: string;
  /** 계정 소속 줄에 적은 직책. 승인하면 배치의 직책이 된다(「팀장」만 칸으로 간다). */
  dutyName: string | null;
  /** 승인하면 나갈 발령. 본직이 없으면 전보(첫 자리), 이미 있으면 겸직이다. */
  suggestedOrderType: OrderType;
  /** 승인이 인사자료(`hr_personnel`)까지 만드는가. 계정만 있는 사람이면 참이다. */
  createsPersonnel: boolean;
  createdAt: string;
};

// ── 홈 위젯 ──────────────────────────────────────────────────────────────────

/**
 * 개인 할일 한 건. 본인만 보고 본인만 고친다.
 *
 * 홈의 TO DO 위젯이 유일한 화면이다 — 전용 페이지를 두지 않는다.
 * 할일 관리를 하러 따로 들어가야 한다면 아무도 쓰지 않는다.
 */
export type TodoItem = {
  id: string;
  userId: string;
  content: string;
  dueDate: string | null;
  done: boolean;
  sortOrder: number;
};

/**
 * 뉴스 섹션 — 화면의 탭 하나이자 수집 대상 피드 하나다.
 *
 * `sectionKey` 는 탭 링크(`/news/headlines?section=business`)가 쓰는 값이라 라벨과 따로 둔다.
 * 라벨을 다듬어도 공유된 주소가 깨지지 않는다.
 */
export type NewsSection = {
  id: string;
  sectionKey: string;
  label: string;
  /** 구글뉴스 RSS 주소. 관리 화면에서만 쓴다. */
  feedUrl: string;
  sortOrder: number;
  active: boolean;
};

/**
 * 뉴스 한 줄. <b>본문이 없다</b> — 제목을 누르면 언론사 원문으로 나간다.
 *
 * 건설동향(`ConstructionTrendPost`)과 다른 타입인 이유는 규칙이 다르기 때문이다. 저쪽은
 * 담당자가 검토해 발행하는 글(초안 상태·본문 있음)이고, 이쪽은 수집 즉시 뜨는 제목 목록이다.
 */
export type NewsItem = {
  id: string;
  sectionId: string;
  title: string;
  /** 언론사. 목록에 늘 함께 그린다 — 제목만 나열하면 사내에서 쓴 글처럼 읽힌다. */
  sourceName: string | null;
  link: string;
  /** 발행 시각(ISO). 피드가 주지 않으면 null 이고, 그때는 목록 뒤로 밀린다. */
  publishedAt: string | null;
};

/**
 * 하늘 상태.
 *
 * `shower`(소나기) · `thunderstorm`(뇌우) 는 `rain` 과 따로 둔다 — 현장에서 가장 다르게 움직이는
 * 셋이기 때문이다. 뇌우는 비의 세기와 무관하게 타워크레인·고소작업을 세우는 사유고, 소나기는
 * 긋고 지나간다. 이슬비는 `rain` 에 포함된다.
 */
export type WeatherCondition = 'clear' | 'partly_cloudy' | 'cloudy' | 'rain' | 'shower' | 'thunderstorm' | 'snow';

/**
 * 한 지점의 오늘 날씨.
 *
 * 기온보다 <b>작업 가능 여부</b>를 먼저 담는다 — 강수, 바람(타워크레인 15m/s 기준),
 * 특보. 현장이 아침에 확인하는 값이 그것이기 때문이다.
 */
export type WeatherForecast = {
  locationName: string;
  observedAt: string;
  condition: WeatherCondition;
  temperature: number;
  feelsLike: number;
  /** 강수확률 % */
  precipitationChance: number;
  /** m/s */
  windSpeed: number;
  humidity: number;
  /** 대기질 조회만 실패하면 null 이다 — 기온·바람은 살리고 이 칸만 비운다. */
  fineDust: '좋음' | '보통' | '나쁨' | '매우나쁨' | null;
  /**
   * 오늘 눈에 띄는 기상 조건. 없으면 빈 배열.
   *
   * **발효된 특보가 아니다** — 무료 2.5 에 특보가 없어서 서버가 관측값의 임계값으로 추정한다
   * (`WeatherService#alertsOf`). 주의보와 같은 지표를 재는 것은 그 이름을 쓰고(`폭염주의보`),
   * 그렇지 않은 것은 잰 것을 그대로 말한다(`강수확률 높음`).
   */
  alerts: string[];
  hourly: Array<{
    hour: number;
    temperature: number;
    precipitationChance: number;
    condition: WeatherCondition;
  }>;
};

export type PersonnelCareer = {
  id: string;
  personnelId: string;
  /** 현장 근무인가 본사 근무인가. 근무 이력의 합계("A현장 2년 · 본사 1년")가 이 구분에서 나온다. */
  workType: CareerWorkType;
  /** 근무한 회사. 비어 있으면 자사 경력, 채워져 있으면 입사 전 다른 회사에서 쌓은 경력이다. */
  company: string | null;
  projectStart: string | null;
  projectEnd: string | null;
  /** 사업명 */
  projectName: string | null;
  /** 발주자 */
  client: string | null;
  /** 공사종류 */
  structure: string | null;
  /** 공사개요 */
  projectOverview: string | null;
  /** 적용공법 */
  method: string | null;
  /** 적용 융복합건설기술 */
  techFusion: string | null;
  /** 직무분야 */
  jobField: string | null;
  /** 전문분야 */
  specialtyField: string | null;
  /** 담당업무 */
  duties: string | null;
  /** 직위 */
  position: string | null;
  /** 책임정도 */
  responsibility: string | null;
  /**
   * 협회(KISCON·건설기술인협회) 신고상태. `APPROVED` 만 역량지수에 들어간다 —
   * 등록은 됐는데 신고가 안 된 구간이 승급이 조용히 미뤄지는 가장 흔한 이유다.
   */
  reportStatus: CareerReportStatus;
  reportedDate: string | null;
  /** 협회가 인정한 일수. 참여율·중복기간 조정으로 사업 기간과 다를 수 있다. */
  recognizedDays: number | null;
  reportNote: string | null;
  sortOrder: number;
};

export type CareerWorkType = 'SITE' | 'HQ';

export const CAREER_WORK_TYPE_LABELS: Record<CareerWorkType, string> = {
  SITE: '현장',
  HQ: '본사',
};

export type CareerReportStatus = 'UNREPORTED' | 'SUBMITTED' | 'APPROVED' | 'REJECTED';

export const CAREER_REPORT_LABELS: Record<CareerReportStatus, string> = {
  UNREPORTED: '미신고',
  SUBMITTED: '신고중',
  APPROVED: '인정',
  REJECTED: '반려',
};
